import React, {useCallback} from 'react';

import {useDropDownContext} from './drop-down-context';

export type TriggerProps = {
	/** инпут, кнопка или любой другой элемент, открывающий выпадашку */
	children: React.ReactElement<Record<string, unknown>>;
};

const setRef = (ref: unknown, element: HTMLElement | null) => {
	if (typeof ref === 'function') (ref as (value: HTMLElement | null) => void)(element);
	else if (ref && typeof ref === 'object') (ref as React.RefObject<HTMLElement | null>).current = element;
};

export const Trigger: React.FC<TriggerProps> = ({children}) => {
	const drop = useDropDownContext();
	const child = children.props;

	const handleRef = useCallback(
		(element: HTMLElement | null) => {
			if (drop) drop.triggerRef.current = element;
			// в React 19 ref приходит обычным пропсом, у наших полей он называется setRef
			setRef(child.ref, element);
			setRef(child.setRef, element);
		},
		[drop, children, child.setRef],
	);

	const handleClick = useCallback(
		(event: React.MouseEvent) => {
			(child.onClick as ((event: React.MouseEvent) => void) | undefined)?.(event);
			if (event.defaultPrevented || !drop || drop.disabled) return;
			// поле ввода только открывает список: клик по тексту не должен его закрывать
			if (drop.inputValue !== undefined) drop.setOpen(true, event);
			else drop.toggle(event);
		},
		[child, drop],
	);

	const handleVirtualKey = useCallback(
		(event: React.KeyboardEvent) => {
			if (!drop) return;
			const list = drop.listController.current;
			switch (event.key) {
				case 'ArrowDown':
					if (!drop.open) drop.setOpen(true, event);
					else list?.move(1);
					event.preventDefault();
					break;
				case 'ArrowUp':
					if (!drop.open || !list) break;
					if ((list.activeIndex() ?? 0) <= 0) list.clear();
					else list.move(-1);
					event.preventDefault();
					break;
				case 'Enter':
					if (drop.open && list?.activate()) event.preventDefault();
					break;
			}
		},
		[drop],
	);

	const handleKeyDown = useCallback(
		(event: React.KeyboardEvent) => {
			if (drop && !drop.disabled && drop.virtualFocus) handleVirtualKey(event);
			(child.onKeyDown as ((event: React.KeyboardEvent) => void) | undefined)?.(event);
			if (event.defaultPrevented || !drop || drop.disabled || drop.virtualFocus) return;
			if (event.key === 'ArrowDown') {
				if (!drop.open) drop.setOpen(true, event);
				requestAnimationFrame(drop.focusFirstItem);
				event.preventDefault();
			}
		},
		[child, drop, handleVirtualKey],
	);

	const handleChange = useCallback(
		(next: string | React.ChangeEvent<HTMLInputElement>) => {
			(child.onChange as ((value: unknown) => void) | undefined)?.(next);
			if (!drop) return;
			const value = typeof next === 'string' ? next : (next?.target?.value ?? '');
			drop.setInputValue(value, next as React.SyntheticEvent);
		},
		[child, drop],
	);

	if (!drop) return children;

	const withInput = drop.inputValue !== undefined;

	return React.cloneElement(children, {
		ref: handleRef,
		onClick: handleClick,
		onKeyDown: handleKeyDown,
		'aria-haspopup': child['aria-haspopup'] ?? (drop.selectMode ? 'listbox' : true),
		'aria-expanded': drop.open,
		'aria-controls': drop.open ? drop.dropdownId : undefined,
		'aria-activedescendant': drop.virtualFocus ? drop.activeDescendant : undefined,
		...(withInput
			? {value: drop.inputValue, onChange: handleChange, role: 'combobox', 'aria-autocomplete': 'list'}
			: null),
	} as Record<string, unknown>);
};
